import {FC, ReactNode, useState, useEffect} from 'react';
import {Button, Layout, Menu, theme, Drawer} from 'antd';
import {MenuOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import logo from "../assets/logo.png";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {colors} from "../util/colors.ts";
import {MenuItem} from "./menuItems.tsx";
import {User} from "../context/AuthContext.tsx";
import {User as ClientUser} from "client/src/context/AuthContext.tsx";
import { Gear, UserCircle, Envelope, Briefcase, Globe, IdentificationCard, PenNib, GraduationCap, Crown, Exam } from '@phosphor-icons/react';

const {Content, Sider, Header} = Layout;

 // TODO: remove comment
// const BOTTOM_MENU_KEYS = ['profile', 'account'];
const BOTTOM_MENU_KEYS = ["arg"];

interface LayoutWrapperProps {
    children: ReactNode;
    menuItems: MenuItem[];
    signOut: () => void;
    isClient?: boolean;
    user?: User | ClientUser
}

export const LayoutWrapperCandidate: FC<LayoutWrapperProps> = ({children, user, menuItems, signOut, isClient}) => {
    const {
        token: {colorBgContainer, borderRadiusLG},
    } = theme.useToken();
    const location = useLocation();
    const navigate = useNavigate()
    const isPrivateUser = (user as ClientUser)?._client?.isPrivate;
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

    const canPostJob = isClient && user?.jobs_current_month === 0

    // Close mobile menu when location changes
    useEffect(() => {
        setMobileMenuVisible(false);
    }, [location]);

    const toggleMobileMenu = () => {
        setMobileMenuVisible(!mobileMenuVisible);
    };

    // Add icons to menu items
    const menuItemsWithIcons = menuItems.map(item => ({
        ...item,
        icon: getIcon(item.key as string),
    }));

    // Define the new static menu items to be added
    // Explicitly type for items in Menu component (approximating Ant Design's ItemType)
    type MenuItemType = {
        key: React.Key; // Use React.Key for key type
        label: React.ReactNode; // Allow JSX elements and strings for label
        icon?: React.ReactNode;
        style?: React.CSSProperties;
        children?: MenuItemType[]; // Allow for submenus if needed
        type?: never; // Ensure type is not 'divider' for regular items
        url?: string;
    };
    type MenuDividerType = {
        type: 'divider';
        key?: React.Key; // Allow optional key for dividers if needed by React
    };
    type CombinedMenuItemType = MenuItemType | MenuDividerType;

    // TODO: remove comment
    const newStaticMenuItems: MenuItemType[] = [];

    // Dynamically construct top menu items, inserting new items and a divider after 'messages'
    const dynamicallyConstructedTopMenuItems: CombinedMenuItemType[] = [];
    menuItemsWithIcons.forEach(item => {
        if (!BOTTOM_MENU_KEYS.includes(item.key as string)) {
            // The item from menuItemsWithIcons should now conform to MenuItemType
            dynamicallyConstructedTopMenuItems.push(item as CombinedMenuItemType);
            if (item.key === 'messages') { // Check for the 'messages' item
                dynamicallyConstructedTopMenuItems.push({ type: 'divider' }); // Add a divider
                newStaticMenuItems.forEach(newItem => {
                    if (!BOTTOM_MENU_KEYS.includes(newItem.key as string)) {
                        dynamicallyConstructedTopMenuItems.push(newItem);
                    }
                });
            }
        }
    });

    const topMenuItems = dynamicallyConstructedTopMenuItems;
    // TODO: remove comment
    const bottomMenuItems = menuItemsWithIcons.filter(item => BOTTOM_MENU_KEYS.includes(item.key as string))
    // const bottomMenuItems = menuItemsWithIcons.filter(item => BOTTOM_MENU_KEYS.includes(item.key as string) && !BOTTOM_MENU_KEYS.includes(item.key as string))
    //                      .map(item => item as CombinedMenuItemType); // Also ensure bottom items conform

    return (
        <Layout style={{minHeight: '100vh'}}>
            {/* Mobile Header with Burger Menu */}
            <MobileHeader style={{backgroundColor: colors.darkBg}}>
                <MobileHeaderContent>
                    <Link to={"/"}>
                        <MobileLogo src={logo as string} alt="Silvertalent logo"/>
                    </Link>
                    <BurgerButton
                        icon={<MenuOutlined/>}
                        onClick={toggleMobileMenu}
                        style={{color: colors.text}}
                    />
                </MobileHeaderContent>
            </MobileHeader>

            {/* Desktop Sidebar */}
            <DesktopSider
                breakpoint="lg"
                collapsedWidth="0"
                theme="dark"
                width={250}
                style={{backgroundColor: colors.darkBg, height: "100vh"}}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <LogoContainer>
                    <Link to={"/"}>
                        <Logo src={logo as string} alt="Silvertalent logo"/>
                    </Link>
                </LogoContainer>
                <Menu
                    theme="light"
                    mode="inline"
                    selectedKeys={[location.pathname.slice(1) || 'pool']}
                    items={topMenuItems}
                    style={{backgroundColor: colors.darkBg}}
                    onClick={({ key }) => {
                        const item = topMenuItems.find(item => item.key === key);
                        if (item && 'url' in item && item.url) {
                            navigate(item.url);
                        }
                    }}
                />
                <div style={{ flexGrow: 1 }} />

                {/* Conditionally render the "Anzeige schalten" button container */}
                {isClient && canPostJob && !location.pathname.match(/^\/(?:jobs|new\/(?:job|profile))$/) && (
                     <div style={{padding: '0 16px 16px 16px'}}> {/* Adjust padding as needed */}
                        <Button
                            onClick={() => navigate(
                                (!isPrivateUser && !(user as ClientUser)?._client?.profile_name)
                                    ? "/new/profile"
                                    : "/new/job"
                            )}
                            style={{backgroundColor: colors.main, color: 'white', border: "none", width: '100%'}} // Button takes full width of its container
                            block
                        >
                            Anzeige schalten
                        </Button>
                    </div>
                )}

                {/* Render bottom menu items if any */}
                {bottomMenuItems.length > 0 && (
                    <Menu
                        theme="light"
                        mode="inline"
                        selectedKeys={[location.pathname.slice(1) || 'pool']}
                        items={bottomMenuItems}
                        style={{backgroundColor: colors.darkBg}}
                    />
                )}
                <div style={{padding: 16}}>
                    <Button onClick={signOut} block style={{ borderColor: colors.bg, color: colors.text }}>Abmelden</Button>
                </div>
                </div>
            </DesktopSider>

            {/* Mobile Full Screen Overlay Menu */}
            <MobileOverlay
                placement="top"
                onClose={toggleMobileMenu}
                open={mobileMenuVisible}
                height="100%"
                styles={{
                    header: { display: 'none' },
                    body: { padding: 0, backgroundColor: colors.darkBg },
                    content: { backgroundColor: colors.darkBg }
                }}
                closeIcon={null}
                mask={false}
                maskClosable={false}
            >
                <MobileMenuContent>
                    <MobileMenuHeader>
                        <Link to={"/"} onClick={toggleMobileMenu}>
                            <MobileLogo src={logo as string} alt="Silvertalent logo"/>
                        </Link>
                        <Button
                            icon={<MenuOutlined/>}
                            onClick={toggleMobileMenu}
                            style={{color: colors.text, border: 'none', backgroundColor: 'transparent'}}
                        />
                    </MobileMenuHeader>

                    <Menu
                        theme="light"
                        mode="vertical"
                        selectedKeys={[location.pathname.slice(1) || 'pool']}
                        items={topMenuItems}
                        style={{
                            backgroundColor: colors.darkBg,
                            border: 'none',
                            fontSize: '18px'
                        }}
                        onClick={({ key }) => {
                            const item = topMenuItems.find(item => item.key === key);
                            if (item && 'url' in item && item.url) {
                                navigate(item.url);
                            }
                            setMobileMenuVisible(false);
                        }}
                    />

                    {/* Mobile Sign Out Button */}
                    <MobileSignOutContainer>
                        <Button
                            onClick={() => {
                                signOut();
                                setMobileMenuVisible(false);
                            }}
                            block
                            size="large"
                            style={{ borderColor: colors.bg, color: colors.text, fontSize: '18px' }}
                        >
                            Abmelden
                        </Button>
                    </MobileSignOutContainer>
                </MobileMenuContent>
            </MobileOverlay>

            <Layout>
                <StyledContent style={{margin: '0', backgroundColor: colors.bg}}>
                    <div
                        style={{
                            padding: 0,
                            minHeight: 360,
                            background: colorBgContainer,
                            borderRadius: borderRadiusLG,
                        }}
                    >
                        {children}
                    </div>
                </StyledContent>
            </Layout>
        </Layout>
    );
};

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid rgb(217 217 217);
`;

const Logo = styled.img`
    cursor: pointer;
    width: 150px;
`;

const StyledContent = styled(Content)`
    padding: 8px 24px 0;
    @media (max-width: 991px) {
        padding: 68px 8px 8px 8px; /* Add top padding for fixed mobile header */
        margin: 0;
    }
    @media (min-width: 992px) {
        @media (max-width: 767px) {
            padding: 0 8px;
        }
    }
`;

const getIcon = (key: string): ReactNode => {
    switch (key) {
        case 'pool':
            return <Briefcase size={18} />;
        case 'profile':
            return <UserCircle size={18} />;
        case 'account':
            return <Gear size={18} />;
        case 'messages':
            return <Envelope size={18} />;
        case 'ai-jobs':
            return <Globe size={18} />;
        case 'cv-generator':
            return <IdentificationCard size={18} />;
        case 'anschreiben-generator':
            return <PenNib size={18} />;
        case 'online-kurse':
            return <GraduationCap size={18} />;
        case 'interview':
            return <Exam size={18} />;
        case 'upgrade':
            return <Crown size={18} />;
        default:
            return null;
    }
};

const MobileHeader = styled(Header)`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    height: 60px;
    line-height: 60px;
    padding: 0;
    
    @media (max-width: 991px) {
        display: block;
    }
`;

const MobileHeaderContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    height: 100%;
`;

const MobileLogo = styled.img`
    height: 40px;
    width: auto;
`;

const BurgerButton = styled(Button)`
    border: none;
    background: transparent;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background: transparent;
        color: ${colors.main};
    }
`;

const DesktopSider = styled(Sider)`
    @media (max-width: 991px) {
        display: none !important;
    }
`;

const MobileOverlay = styled(Drawer)`
    .ant-drawer-content {
        background-color: ${colors.darkBg} !important;
        transition: none !important;
        animation: none !important;
    }
    
    .ant-drawer-body {
        background-color: ${colors.darkBg} !important;
        height: 100vh;
        display: flex;
        flex-direction: column;
    }
    
    &.ant-drawer {
        .ant-drawer-content-wrapper {
            transition: none !important;
            animation: none !important;
        }
        
        .ant-drawer-content {
            transition: none !important;
            animation: none !important;
        }
    }
`;

const MobileMenuContent = styled.div`
    height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: ${colors.darkBg};
`;

const MobileMenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid rgb(217 217 217);
    margin-bottom: 16px;
`;

const MobileSignOutContainer = styled.div`
    margin-top: auto;
    padding: 24px 16px;
`;
